import clsx from 'clsx';
import React, { forwardRef, useId, useImperativeHandle, useRef } from 'react';
import { flushSync } from 'react-dom';
import { aggregate, useControlledValue, useCurrent, useIsomorphicLayoutEffect, usePrevious, useResponsiveProp, } from '../../utils';
import { FormField, helperTextId, labelId, validationTextId, } from '../FormField';
import { TextField } from '../TextField';
import './TwoFactorField.css';
export const TwoFactorField = forwardRef(function TwoFactorField(props, ref) {
    var _a;
    const { name, helperText, validationText, label, length = 6, id: userId, value: userValue, defaultValue, onChange, onComplete, className, ariaInputLabel, autoFocus, disabled, variant = 'inline', ...rest } = props;
    const inputRefs = useRef([]);
    const [value, setValue] = useControlledValue({
        value: userValue,
        onChange: (nextValue) => {
            const changed = !(nextValue === null || nextValue === void 0 ? void 0 : nextValue.every((str, index) => (previousValue === null || previousValue === void 0 ? void 0 : previousValue[index]) === str));
            if (changed && (nextValue === null || nextValue === void 0 ? void 0 : nextValue.filter((str) => str !== '').length) === length) {
                onComplete === null || onComplete === void 0 ? void 0 : onComplete(nextValue);
            }
            onChange === null || onChange === void 0 ? void 0 : onChange(nextValue);
        },
        defaultValue,
    });
    const valueRef = useCurrent(value);
    const previousValue = usePrevious(value);
    const responsiveVariant = useResponsiveProp(variant);
    const uniqueId = useId();
    const id = userId !== null && userId !== void 0 ? userId : uniqueId;
    const labelledBy = label ? labelId(id) : undefined;
    const describedBy = aggregate(validationText ? validationTextId(id) : undefined, helperText ? helperTextId(id) : undefined);
    useImperativeHandle(ref, () => ({
        focus() {
            var _a;
            (_a = inputRefs.current[0]) === null || _a === void 0 ? void 0 : _a.focus();
        },
    }), []);
    useIsomorphicLayoutEffect(() => {
        inputRefs.current.forEach((node, index) => {
            var _a;
            if (node) {
                node.value = (_a = value === null || value === void 0 ? void 0 : value[index]) !== null && _a !== void 0 ? _a : '';
            }
        });
    }, [value]);
    return (React.createElement(FormField, { ...rest, label: label, labelElementType: "div", htmlFor: id, helperText: helperText, validationText: validationText, className: clsx('mollie-ui-two-factor-field', {
            [`mollie-ui-two-factor-field--${responsiveVariant}`]: responsiveVariant,
        }, className) },
        React.createElement("div", { role: "group", id: id, className: "mollie-ui-two-factor-field-group", "aria-labelledby": labelledBy || undefined, "aria-describedby": describedBy || undefined },
            React.createElement("input", { type: "text", className: "mollie-ui-visually-hidden", "aria-hidden": "true", tabIndex: -1, autoComplete: "one-time-code", name: name, value: (_a = value === null || value === void 0 ? void 0 : value.join('')) !== null && _a !== void 0 ? _a : '', onInput: (e) => {
                    const value = e.target.value.split('');
                    setValue(value);
                    value.forEach((digit, index) => {
                        const input = inputRefs.current[index];
                        if (input) {
                            input.value = digit;
                        }
                    });
                } }),
            Array.from({ length }).map((_, fieldIndex) => (React.createElement(TextField, { key: fieldIndex, "aria-label": ariaInputLabel === null || ariaInputLabel === void 0 ? void 0 : ariaInputLabel(fieldIndex + 1, length), disabled: disabled, inputMode: "numeric", onKeyDown: (e) => {
                    var _a, _b, _c;
                    // skip when pasting!
                    if (e.key === 'v' && e.metaKey) {
                        e.currentTarget.value = '';
                        return;
                    }
                    if (!e.metaKey && /^\D$/.test(e.key)) {
                        e.preventDefault();
                    }
                    if (/\d/.test(e.key)) {
                        e.preventDefault();
                        const currentTarget = e.currentTarget;
                        flushSync(() => {
                            setValue((value) => {
                                return Array.from({ length }).map((_, index) => {
                                    var _a;
                                    if (fieldIndex === index) {
                                        return e.key;
                                    }
                                    return (_a = value === null || value === void 0 ? void 0 : value[index]) !== null && _a !== void 0 ? _a : '';
                                });
                            });
                        });
                        currentTarget.value = e.key;
                        (_a = inputRefs.current[fieldIndex + 1]) === null || _a === void 0 ? void 0 : _a.focus();
                    }
                    if (e.key === 'Backspace') {
                        const currentTargetValue = e.currentTarget.value;
                        e.preventDefault();
                        flushSync(() => {
                            setValue((value) => {
                                return Array.from({ length }).map((_, index) => {
                                    var _a;
                                    if ((value === null || value === void 0 ? void 0 : value[index + 1]) === '' &&
                                        fieldIndex - 1 === index) {
                                        return '';
                                    }
                                    else if (fieldIndex === index) {
                                        return '';
                                    }
                                    return (_a = value === null || value === void 0 ? void 0 : value[index]) !== null && _a !== void 0 ? _a : '';
                                });
                            });
                        });
                        const targetInput = inputRefs.current[fieldIndex - 1];
                        if (targetInput) {
                            if (currentTargetValue !== '') {
                                e.currentTarget.value = '';
                            }
                            else {
                                targetInput.focus();
                                targetInput.value = '';
                            }
                        }
                        else {
                            e.currentTarget.value = '';
                        }
                    }
                    if (e.key === 'ArrowLeft') {
                        e.preventDefault();
                        (_b = inputRefs.current[fieldIndex - 1]) === null || _b === void 0 ? void 0 : _b.focus();
                    }
                    if (e.key === 'ArrowRight') {
                        e.preventDefault();
                        (_c = inputRefs.current[fieldIndex + 1]) === null || _c === void 0 ? void 0 : _c.focus();
                    }
                }, onPaste: (e) => {
                    e.preventDefault();
                    const textData = e.clipboardData
                        .getData('Text')
                        .replaceAll(/[^\d]/g, '');
                    if (textData) {
                        flushSync(() => {
                            setValue((value) => {
                                var _a;
                                return [
                                    ...Array.from({ length: fieldIndex }).map((_, index) => {
                                        var _a;
                                        return (_a = value === null || value === void 0 ? void 0 : value[index]) !== null && _a !== void 0 ? _a : '';
                                    }),
                                    ...textData.split(''),
                                    ...((_a = value === null || value === void 0 ? void 0 : value.slice(fieldIndex + textData.length)) !== null && _a !== void 0 ? _a : []),
                                ].slice(0, length);
                            });
                        });
                        setTimeout(() => {
                            var _a;
                            const currentValue = valueRef.current;
                            if (currentValue) {
                                currentValue.forEach((digit, index) => {
                                    const input = inputRefs.current[index];
                                    if (input) {
                                        input.value = digit;
                                    }
                                });
                                const focusIndex = Math.min(fieldIndex + textData.length, length - 1);
                                (_a = inputRefs.current[focusIndex]) === null || _a === void 0 ? void 0 : _a.focus();
                            }
                        });
                    }
                }, size: 1, className: "mollie-ui-two-factor-field-text-field", UNSAFE_textFieldClassName: clsx({
                    'mollie-ui-text-field--invalid': !!validationText,
                }), UNSAFE_inputClassName: "mollie-ui-two-factor-field-input", inputRef: (node) => {
                    inputRefs.current[fieldIndex] = node;
                }, autoFocus: fieldIndex === 0 ? autoFocus : undefined, onInput: (e) => {
                    const firstChar = e.currentTarget.value.charAt(0);
                    flushSync(() => {
                        setValue((value) => {
                            return Array.from({ length }).map((_, index) => {
                                var _a;
                                if (fieldIndex === index) {
                                    return firstChar;
                                }
                                return (_a = value === null || value === void 0 ? void 0 : value[index]) !== null && _a !== void 0 ? _a : '';
                            });
                        });
                    });
                } }))))));
});
//# sourceMappingURL=TwoFactorField.js.map